// platformIntroTour — step definitions for the platform intro walkthrough,
// aimed at users who are new to the platform rather than at any one feature.
// Anchors match the data-tour attributes added to the builder primitives:
//
//   get-started-banner (the banner above the canvas, step 0)
//   category-new-badge (the "New" badge on the first badged dictionary
//     category — querySelector returns the first, i.e. the topmost category)
//   variables-dropzone (zoneType="include")
//   variable-card (any leaf in the tree — querySelector returns the first)
//   variable-card-remove (the top-right "×" on the first leaf)
//   connector-pill (any pill in the tree — querySelector returns the first)
//   group-bracket (the nested OR sub-group's bracket)
//   ungroup-button (the "Ungroup" pill on the nested OR group)
//   plain-language-summary
//   plain-language-summary-copy (the "Copy" button next to the summary)
//
// Bump `version` whenever the step copy or list changes materially — that
// re-shows the tour to users who already completed v(N-1).

import type { TourDefinition } from '../types';

export const PLATFORM_INTRO_TOUR_ID = 'platformIntro';
export const PLATFORM_INTRO_TOUR_VERSION = 1;

export const platformIntroTour: TourDefinition = {
  id: PLATFORM_INTRO_TOUR_ID,
  version: PLATFORM_INTRO_TOUR_VERSION,
  steps: [
    {
      id: 'banner',
      anchor: 'get-started-banner',
      title: 'Welcome to the audience builder',
      text: 'New to the platform? This 60-second tour shows how to build an audience — drag attributes in, combine them with AND / OR, and check your logic before you pull counts.',
      placement: 'bottom',
    },
    {
      id: 'new-categories',
      anchor: 'category-new-badge',
      title: 'New in this data refresh',
      text: 'Categories marked New were added or rebuilt in the latest data refresh. Open one to see the attributes it brings.',
      placement: 'right',
    },
    {
      id: 'dropzone',
      anchor:
        '[data-tour="variables-dropzone"][data-tour-zone="include"]',
      title: 'The Include canvas',
      text: 'Drag attributes from the Data Dictionary on the left into here. Each one becomes a card in your expression tree.',
      placement: 'left',
    },
    {
      id: 'card',
      anchor: 'variable-card',
      title: 'A variable card',
      text: 'Each card is a leaf — one filter on one attribute. Drag a card onto another card to wrap them into a sub-group.',
      placement: 'right',
    },
    {
      id: 'remove',
      anchor: 'variable-card-remove',
      title: 'Two ways to remove a card',
      text: 'Click the × in the top-right corner, or tick the checkbox on the left of the card — either control removes the leaf from your expression.',
      placement: 'left',
    },
    {
      id: 'pill',
      anchor: 'connector-pill',
      title: 'The AND / OR pill',
      text: 'Click to flip between AND and OR. The change applies to every sibling in the same group, and the live summary above updates instantly.',
      placement: 'right',
    },
    {
      id: 'group',
      anchor: 'group-bracket',
      title: 'Nested groups + depth colors',
      text: 'Drop one card onto another and they form a sub-group. Each nesting level gets its own pill color so you can trace logic at a glance: black → teal → blue → orange.',
      placement: 'right',
    },
    {
      id: 'ungroup',
      anchor: 'ungroup-button',
      title: 'Flatten a sub-group',
      text: 'Click Ungroup to dissolve a sub-group — its children move up to the parent and the surrounding operator takes over. Useful when you no longer need the nested logic.',
      placement: 'left',
    },
    {
      id: 'summary',
      anchor: 'plain-language-summary',
      title: 'Plain-language summary',
      text: 'A live, human-readable rendering of your tree. Sanity-check before pulling counts.',
      placement: 'bottom',
    },
    {
      id: 'summary-copy',
      anchor: 'plain-language-summary-copy',
      title: 'Copy your audience logic',
      text: 'Click Copy to grab the rendered audience logic — handy for sharing with teammates or pasting into an AI.',
      placement: 'left',
    },
  ],
};
